const VIN_RE = /\b[A-HJ-NPR-Z0-9]{17}\b/g;

const pad = (n) => String(n).padStart(2, "0");

function endOfMonth(year, month) {
  const d = new Date(Date.UTC(year, month, 0));
  return `${year}-${pad(month)}-${pad(d.getUTCDate())}`;
}

function toIsoDate(day, month, year) {
  const d = new Date(Date.UTC(year, month - 1, day));
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) return null;
  return `${year}-${pad(month)}-${pad(day)}`;
}

const INSPECTION_KEYWORDS = [
  "техосмотр",
  "технический осмотр",
  "действителен до",
  "ülevaatus",
  "ulevaatus",
  "kehtib kuni",
  "technical inspection",
  "inspection valid",
  "valid until",
  "roadworthiness",
];


const PLATE_TOKEN_RE = /\b[A-Z0-9]{5,8}\b/g;

const DIGIT_REPAIR = { O: "0", Q: "0", D: "0", I: "1", L: "1", Z: "2", S: "5", G: "6", B: "8" };
const LETTER_REPAIR = { "0": "O", "1": "I", "2": "Z", "5": "S", "6": "G", "8": "B" };

function repairStandardEstonianPlate(token) {
  const raw = String(token || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (raw.length !== 6) return null;

  const chars = raw.split("");
  for (let i = 0; i < 3; i += 1) {
    if (!/\d/.test(chars[i])) chars[i] = DIGIT_REPAIR[chars[i]] || chars[i];
  }
  for (let i = 3; i < 6; i += 1) {
    if (!/[A-Z]/.test(chars[i])) chars[i] = LETTER_REPAIR[chars[i]] || chars[i];
  }

  const repaired = chars.join("");
  return /^\d{3}[A-Z]{3}$/.test(repaired) ? repaired : null;
}

function extractRegistrationNumber(text) {
  const normalized = normalizeOcrText(text).toUpperCase();
  const lines = normalized.split(/\n+/).map((line) => line.trim()).filter(Boolean);

  // IMPORTANT: never guess a registration number from arbitrary technical
  // values (engine size, registration certificate number, policy number, etc.).
  // Transpordiamet's result header contains the plate next to/above an explicit
  // VIN line. We accept a plate only from that header context.
  const vinLineIndexes = [];
  lines.forEach((line, index) => {
    if (/\bVIN\s*[:：]?/i.test(line)) vinLineIndexes.push(index);
  });
  if (!vinLineIndexes.length) return null;

  const candidates = [];
  const addPlateCandidates = (line, distance) => {
    const compact = String(line || '').toUpperCase().replace(/[^A-Z0-9\s]/g, ' ');

    // Standard Estonian plate may be rendered as "540TFT" or "540 TFT".
    const spaced = compact.match(/\b[A-Z0-9]{3}\s+[A-Z0-9]{3}\b/g) || [];
    spaced.forEach((match) => {
      const repaired = repairStandardEstonianPlate(match.replace(/\s+/g, ''));
      if (repaired) candidates.push({ value: repaired, score: 100 - distance * 10 });
    });

    const tokens = compact.match(/\b[A-Z0-9]{6}\b/g) || [];
    tokens.forEach((token) => {
      const repaired = repairStandardEstonianPlate(token);
      if (repaired) candidates.push({ value: repaired, score: 95 - distance * 10 });
    });
  };

  vinLineIndexes.forEach((vinIndex) => {
    // Plate is normally 1–3 visual lines before VIN. Allow the same line for
    // OCR layouts where the header is merged, but never scan the whole page.
    for (let i = Math.max(0, vinIndex - 3); i <= vinIndex; i += 1) {
      const beforeVin = lines[i].split(/\bVIN\s*[:：]?/i)[0];
      addPlateCandidates(beforeVin, vinIndex - i);
    }
  });

  if (!candidates.length) return null;
  candidates.sort((a, b) => b.score - a.score);
  return candidates[0].value;
}
const REGISTRATION_KEYWORDS = [
  "первичная регистрация",
  "first registration",
  "esmane registreerimine",
  "registration date",
];

function normalizeOcrText(text) {
  return String(text || "")
    .replace(/\r/g, "\n")
    .replace(/[|]/g, "I")
    .replace(/[‐‑‒–—]/g, "-")
    .replace(/[«»“”]/g, '"')
    .replace(/[\t]+/g, " ")
    .replace(/[ ]{2,}/g, " ");
}

function contextScore(context) {
  const c = String(context || "").toLowerCase();
  let score = 0;
  if (INSPECTION_KEYWORDS.some((k) => c.includes(k))) score += 100;
  if (REGISTRATION_KEYWORDS.some((k) => c.includes(k))) score -= 80;
  return score;
}

function addCandidate(candidates, { iso, precision, index, raw, text, baseScore = 0 }) {
  if (!iso) return;
  const before = text.slice(Math.max(0, index - 110), index);
  const after = text.slice(index, Math.min(text.length, index + String(raw || "").length + 110));
  const context = `${before} ${after}`;
  candidates.push({
    iso,
    precision,
    score: baseScore + contextScore(context),
    context,
    raw,
  });
}

function extractInspectionDate(text) {
  const normalized = normalizeOcrText(text);
  const currentYear = new Date().getFullYear();
  const candidates = [];

  // Exact DD.MM.YYYY / DD MM YYYY. Exact dates get preference when they are
  // explicitly close to inspection wording.
  const exactRe = /\b(0?[1-9]|[12]\d|3[01])\s*[.\/-]\s*(0?[1-9]|1[0-2])\s*[.\/-]\s*((?:20)?\d{2})\b/g;
  for (const m of normalized.matchAll(exactRe)) {
    const day = Number(m[1]);
    const month = Number(m[2]);
    let year = Number(m[3]);
    if (year < 100) year += 2000;
    if (year < currentYear - 2 || year > currentYear + 8) continue;
    addCandidate(candidates, {
      iso: toIsoDate(day, month, year),
      precision: "day",
      index: m.index ?? 0,
      raw: m[0],
      text: normalized,
      baseScore: 45,
    });
  }

  // Month/year is what Transpordiamet commonly renders: 12.2026.
  // OCR may lose the punctuation, therefore separators are intentionally
  // permissive: 12.2026, 12/2026, 12-2026, 12 2026 and "12. 2026".
  const monthYearRe = /\b(0?[1-9]|1[0-2])\s*(?:[.\/-]\s*|\s+)((?:20)?\d{2})\b/g;
  for (const m of normalized.matchAll(monthYearRe)) {
    const month = Number(m[1]);
    let year = Number(m[2]);
    if (year < 100) year += 2000;
    if (year < currentYear - 2 || year > currentYear + 8) continue;
    addCandidate(candidates, {
      iso: endOfMonth(year, month),
      precision: "month",
      index: m.index ?? 0,
      raw: m[0],
      text: normalized,
      baseScore: 35,
    });
  }

  // Last-resort OCR repair for cases where the separator disappears entirely
  // near inspection text, e.g. "до 122026" / "kuni 122026".
  const compactRe = /\b(0[1-9]|1[0-2])\s*(20\d{2})\b/g;
  for (const m of normalized.matchAll(compactRe)) {
    const month = Number(m[1]);
    const year = Number(m[2]);
    if (year < currentYear - 2 || year > currentYear + 8) continue;
    const localContext = normalized.slice(Math.max(0, (m.index ?? 0) - 100), (m.index ?? 0) + m[0].length + 100);
    if (!INSPECTION_KEYWORDS.some((k) => localContext.toLowerCase().includes(k))) continue;
    addCandidate(candidates, {
      iso: endOfMonth(year, month),
      precision: "month",
      index: m.index ?? 0,
      raw: m[0],
      text: normalized,
      baseScore: 25,
    });
  }

  if (!candidates.length) return null;

  // Context is more important than simply taking the latest date. This prevents
  // "30.10.2002" (first registration) from winning over inspection validity.
  candidates.sort((a, b) => b.score - a.score || b.iso.localeCompare(a.iso));
  return candidates[0];
}

function extractVin(text) {
  const normalized = normalizeOcrText(text).toUpperCase();

  // Only trust a value explicitly labelled VIN. A random 17-character OCR
  // token elsewhere on the page is not enough evidence to identify a vehicle.
  const labeled = normalized.match(/\bVIN\s*[:：]?\s*([A-HJ-NPR-Z0-9][A-HJ-NPR-Z0-9\s._-]{15,30})/i);
  if (!labeled) return null;

  const candidate = String(labeled[1] || '')
    .toUpperCase()
    .replace(/[^A-HJ-NPR-Z0-9]/g, '')
    .slice(0, 17);
  return /^[A-HJ-NPR-Z0-9]{17}$/.test(candidate) ? candidate : null;
}

function extractVehicleHeaderData(text, registrationNumber, vin) {
  const normalized = normalizeOcrText(text);
  const lines = normalized.split(/\n+/).map((line) => line.trim()).filter(Boolean);
  const plateUpper = String(registrationNumber || "").toUpperCase();
  const vinUpper = String(vin || "").toUpperCase();

  const cleanVehicleLine = (value) =>
    String(value || "")
      .replace(/[^A-Za-zÄÖÜÕŠŽäöüõšž0-9 -]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const isPlausibleVehicleLine = (value) => {
    const line = cleanVehicleLine(value);
    if (!line || !/[A-Za-zÄÖÜÕŠŽäöüõšž]{3,}/.test(line)) return false;
    if (/^(VIN|REG|REGISTREERIMIS|РЕГИСТРАЦ|ГЛАВНАЯ|TRANSPORDIAMET|ОСНОВНЫЕ ДАННЫЕ|КОНТРОЛЬ)/i.test(line)) return false;
    if (/техосмотр|страхов|ülevaatus|kindlustus|inspection|insurance/i.test(line)) return false;
    return true;
  };

  let vehicleLine = null;

  // Strongest signal for the Transpordiamet result header:
  //   540TFT
  //   VOLKSWAGEN CARAVELLE
  //   VIN: WV2...
  // Extract only the text between the detected plate and VIN. This survives
  // OCR layouts where all three visual lines are merged into one text block.
  if (plateUpper && vinUpper) {
    const upper = normalized.toUpperCase();
    const platePos = upper.indexOf(plateUpper);
    const vinPos = upper.indexOf(vinUpper, platePos >= 0 ? platePos + plateUpper.length : 0);
    if (platePos >= 0 && vinPos > platePos) {
      const between = normalized.slice(platePos + plateUpper.length, vinPos)
        .replace(/\bVIN\s*:?\s*$/i, "")
        .trim();
      const betweenLines = between.split(/\n+/).map(cleanVehicleLine).filter(isPlausibleVehicleLine);
      if (betweenLines.length) vehicleLine = betweenLines[betweenLines.length - 1];
      else if (isPlausibleVehicleLine(between)) vehicleLine = cleanVehicleLine(between);
    }
  }

  // Second signal: the line immediately after the registration number.
  if (!vehicleLine && plateUpper) {
    const plateIndex = lines.findIndex((line) => line.replace(/\s+/g, "").toUpperCase().includes(plateUpper));
    if (plateIndex >= 0) {
      for (let i = plateIndex + 1; i <= Math.min(lines.length - 1, plateIndex + 4); i += 1) {
        const candidate = cleanVehicleLine(lines[i]);
        if (vinUpper && candidate.toUpperCase().includes(vinUpper)) break;
        if (isPlausibleVehicleLine(candidate)) { vehicleLine = candidate; break; }
      }
    }
  }

  // Fallback: search just above the VIN line, which was the original strategy.
  if (!vehicleLine) {
    let vinIndex = lines.findIndex((line) => vinUpper && line.toUpperCase().includes(vinUpper));
    if (vinIndex < 0) vinIndex = lines.findIndex((line) => /\bVIN\s*:/i.test(line));
    if (vinIndex > 0) {
      for (let i = vinIndex - 1; i >= Math.max(0, vinIndex - 4); i -= 1) {
        const candidate = cleanVehicleLine(lines[i]);
        if (plateUpper && candidate.replace(/\s+/g, "").toUpperCase() === plateUpper) continue;
        if (isPlausibleVehicleLine(candidate)) { vehicleLine = candidate; break; }
      }
    }
  }

  let make = null;
  let model = null;
  if (vehicleLine) {
    const parts = vehicleLine.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      make = parts.shift() || null;
      model = parts.join(" ") || null;
    }
  }

  let year = null;
  const regMatchers = [
    /(?:первичная регистрация|first registration|esmane registreerimine)[^0-9]{0,30}(\d{1,2})[.\/-](\d{1,2})[.\/-](\d{4})/i,
    /(?:первичная регистрация|first registration|esmane registreerimine)[^0-9]{0,30}(\d{4})/i,
  ];
  for (const re of regMatchers) {
    const m = normalized.match(re);
    if (m) { year = Number(m[m.length - 1]); break; }
  }

  return { make, model, year };
}

const INSURANCE_SECTION_KEYWORDS = [
  "данные о дорожном страховании",
  "действие дорожного страхования",
  "дорожная страховка действительна",
  "liikluskindlustus",
  "kindlustusandja",
  "road traffic insurance",
  "motor insurance",
  "insurance company",
];

const INSURANCE_ACTIVE_KEYWORDS = [
  "дорожная страховка действительна",
  "страховка действительна",
  "kindlustus kehtib",
  "liikluskindlustus kehtib",
  "insurance is valid",
  "insurance valid",
];

const INSURANCE_EXPIRED_KEYWORDS = [
  "страховка недействительна",
  "страховка не действительна",
  "kindlustus ei kehti",
  "insurance is not valid",
  "insurance expired",
];

const FUTURE_WORDS = ["в будущем", "tulevikus", "future", "in future"];
const INSURANCE_TYPES = ["договор", "leping", "contract", "полис", "poliis", "policy"];

function parseExactDateToken(value) {
  const m = String(value || "").match(/\b(0?[1-9]|[12]\d|3[01])\s*[.\/-]\s*(0?[1-9]|1[0-2])\s*[.\/-]\s*((?:20)?\d{2})\b/);
  if (!m) return null;
  let year = Number(m[3]);
  if (year < 100) year += 2000;
  return toIsoDate(Number(m[1]), Number(m[2]), year);
}

function cleanInsuranceDocumentToken(value) {
  let token = String(value || "").toUpperCase().replace(/[^A-Z0-9@]/g, "");
  // Tesseract frequently reads the leading C in Estonian policy numbers as @.
  if (/^@\d{8,}$/.test(token)) token = `C${token.slice(1)}`;
  return /^[A-Z0-9]{8,20}$/.test(token) ? token : null;
}

function extractInsuranceData(text) {
  const normalized = normalizeOcrText(text);
  const lower = normalized.toLowerCase();
  let start = -1;
  for (const keyword of INSURANCE_SECTION_KEYWORDS) {
    const idx = lower.indexOf(keyword);
    if (idx >= 0 && (start < 0 || idx < start)) start = idx;
  }
  if (start < 0) {
    return {
      insurance_detected: false,
      insurance_document_number: null,
      insurance_provider: null,
      insurance_type: null,
      insurance_valid_from: null,
      insurance_valid_until: null,
      insurance_status: "unknown",
    };
  }

  // The insurance table is compact. Limiting the OCR window avoids dates and
  // identifiers from unrelated sections lower on the page.
  const section = normalized.slice(start, Math.min(normalized.length, start + 3200));
  const sectionLower = section.toLowerCase();
  const lines = section.split(/\n+/).map((line) => line.trim()).filter(Boolean);

  let insuranceStatus = "unknown";
  if (INSURANCE_EXPIRED_KEYWORDS.some((keyword) => sectionLower.includes(keyword))) insuranceStatus = "expired";
  else if (INSURANCE_ACTIVE_KEYWORDS.some((keyword) => sectionLower.includes(keyword))) insuranceStatus = "active";

  let documentNumber = null;
  let provider = null;
  const headerIndex = lines.findIndex((line) => {
    const x = line.toLowerCase();
    const hasDoc = x.includes("номер документа") || x.includes("dokumendi") || x.includes("document number");
    const hasProvider = x.includes("страховщик") || x.includes("kindlustusandja") || x.includes("insurer") || x.includes("insurance company");
    return hasDoc || hasProvider;
  });

  // Only parse policy details when the actual insurance table is visible.
  // The summary line "insurance is valid" alone is not enough evidence: OCR
  // column ordering can otherwise make unrelated dates (e.g. first
  // registration) look like insurance dates.
  if (headerIndex < 0) {
    return {
      insurance_detected: insuranceStatus !== "unknown",
      insurance_document_number: null,
      insurance_provider: null,
      insurance_type: null,
      insurance_valid_from: null,
      insurance_valid_until: null,
      insurance_status: insuranceStatus,
    };
  }

  const candidateLines = lines.slice(headerIndex + 1, headerIndex + 8);
  const tableText = lines.slice(headerIndex, headerIndex + 10).join("\n");
  const tableLower = tableText.toLowerCase();

  // Strong direct match for Estonian insurance document numbers such as C0050998000.
  const directDocumentMatch = tableText.toUpperCase().match(/\b[A-Z][0-9]{8,14}\b/);
  if (directDocumentMatch) documentNumber = cleanInsuranceDocumentToken(directDocumentMatch[0]);

  for (const line of candidateLines) {
    const parts = line.split(/\s+/);
    if (!parts.length) continue;
    let docIndex = -1;
    let doc = null;
    for (let i = 0; i < Math.min(parts.length, 4); i += 1) {
      let candidate = null;
      if (/^\d{8,20}$/.test(parts[i]) && i > 0 && /^[@©]$/.test(parts[i - 1])) {
        candidate = `C${parts[i]}`;
      } else {
        candidate = cleanInsuranceDocumentToken(parts[i]);
      }
      if (candidate) { docIndex = i; doc = candidate; break; }
    }
    if (!doc) continue;
    documentNumber = documentNumber || doc;
    const rawToken = parts[docIndex];
    const tokenPos = line.indexOf(rawToken);
    const rest = tokenPos >= 0 ? line.slice(tokenPos + rawToken.length).trim() : "";
    if (rest && !/^\d/.test(rest)) {
      let cleaned = rest.replace(/[|]+$/g, "").trim();
      cleaned = cleaned
        .replace(/\b(договор|leping|contract|полис|poliis|policy)\b.*$/i, "")
        .replace(/\b\d{1,2}[.\/-]\d{1,2}[.\/-]\d{4}\b.*$/i, "")
        .trim();
      if (cleaned) provider = cleaned;
    }
    break;
  }

  let insuranceType = null;
  for (const type of INSURANCE_TYPES) {
    const idx = tableLower.indexOf(type);
    if (idx >= 0) {
      insuranceType = tableText.slice(idx, idx + type.length).trim();
      break;
    }
  }

  const exactDates = [];
  const dateRe = /\b(0?[1-9]|[12]\d|3[01])\s*[.\/-]\s*(0?[1-9]|1[0-2])\s*[.\/-]\s*((?:20)?\d{2})\b/g;
  for (const match of tableText.matchAll(dateRe)) {
    const iso = parseExactDateToken(match[0]);
    if (iso) exactDates.push({ iso, index: match.index ?? 0, raw: match[0] });
  }

  // Prefer dates near the "valid from / valid until" table header. In the
  // Transpordiamet screenshot the first table date is the insurance start date.
  let validFrom = exactDates[0]?.iso || null;
  let validUntil = exactDates[1]?.iso || null;

  const hasFuture = FUTURE_WORDS.some((word) => tableLower.includes(word));
  if (hasFuture && !validUntil) {
    insuranceStatus = insuranceStatus === "unknown" ? "future" : insuranceStatus;
  }
  if (validUntil) {
    insuranceStatus = validUntil < new Date().toISOString().slice(0, 10) ? "expired" : "active";
  }

  // If OCR returned a line such as "Договор 24.02.2026 В будущем", use that
  // line as a stronger signal for type/start date/status than arbitrary dates.
  for (const line of candidateLines) {
    const low = line.toLowerCase();
    if (!INSURANCE_TYPES.some((type) => low.includes(type))) continue;
    const date = parseExactDateToken(line);
    if (date) validFrom = date;
    if (FUTURE_WORDS.some((word) => low.includes(word)) && !validUntil) {
      insuranceStatus = insuranceStatus === "unknown" ? "future" : insuranceStatus;
    }
    break;
  }

  return {
    insurance_detected: true,
    insurance_document_number: documentNumber,
    insurance_provider: provider,
    insurance_type: insuranceType,
    insurance_valid_from: validFrom,
    insurance_valid_until: validUntil,
    insurance_status: insuranceStatus,
  };
}

function buildParsedResult(result, identityResult = null) {
  const text = result?.data?.text || "";
  const identityText = identityResult?.data?.text || text;
  const date = extractInspectionDate(text);

  // Vehicle identity is read from the page header. When multilingual OCR
  // struggles with Latin plate characters, an optional English-only header
  // OCR result is used. We never infer plate/VIN from unrelated page values.
  const vin = extractVin(identityText) || extractVin(text);
  const registrationNumber = extractRegistrationNumber(identityText) || extractRegistrationNumber(text);
  const insurance = extractInsuranceData(text);
  const headerVehicle = extractVehicleHeaderData(identityText, registrationNumber, vin);
  const fullVehicle = identityText === text
    ? headerVehicle
    : extractVehicleHeaderData(text, registrationNumber, vin);
  const confidenceValue = Number(result?.data?.confidence || 0);
  const confidence = confidenceValue >= 80 ? "high" : confidenceValue >= 55 ? "medium" : "low";

  return {
    inspection_valid_until: date?.iso || null,
    date_precision: date?.precision || null,
    vin,
    registration_number: registrationNumber,
    make: headerVehicle.make || fullVehicle.make || null,
    model: headerVehicle.model || fullVehicle.model || null,
    year: fullVehicle.year || headerVehicle.year || null,
    ...insurance,
    confidence,
    ocr_confidence: confidenceValue,
    engine: identityResult ? "browser_tesseract_header_fallback" : "browser_tesseract",
    inspection_match: date?.raw || null,
  };
}

function validateScreenshotFile(file) {
  if (!file) throw new Error("Screenshot is required");
  if (!/^image\/(png|jpeg)$/.test(file.type || "")) {
    throw new Error("Only PNG and JPEG screenshots are supported");
  }
}

async function getHeaderRectangle(file) {
  try {
    if (typeof createImageBitmap !== "function") return null;
    const bitmap = await createImageBitmap(file);
    const rectangle = {
      top: 0,
      left: 0,
      width: bitmap.width,
      height: Math.max(140, Math.min(bitmap.height, Math.round(bitmap.height * 0.30))),
    };
    bitmap.close?.();
    return rectangle;
  } catch {
    return null;
  }
}

async function createFleetHeaderOcrWorker() {
  const { createWorker } = await import("tesseract.js");
  return createWorker("eng", 1);
}

async function recognizeVehicleHeader(worker, file) {
  const rectangle = await getHeaderRectangle(file);
  return worker.recognize(file, rectangle ? { rectangle } : undefined);
}

function needsIdentityFallback(parsed) {
  return !parsed?.registration_number || !parsed?.vin || !parsed?.make || !parsed?.model;
}

async function createFleetOcrWorker(progressRef) {
  const { createWorker } = await import("tesseract.js");
  return createWorker("rus+est+eng", 1, {
    logger: (message) => {
      if (message?.status === "recognizing text" && typeof message.progress === "number") {
        progressRef.current?.(Math.round(message.progress * 100));
      }
    },
  });
}

export async function parseVehicleScreenshotInBrowser(file, onProgress) {
  validateScreenshotFile(file);
  const progressRef = { current: onProgress };
  const worker = await createFleetOcrWorker(progressRef);
  let headerWorker = null;
  try {
    const fullResult = await worker.recognize(file);
    let parsed = buildParsedResult(fullResult);

    if (needsIdentityFallback(parsed)) {
      headerWorker = await createFleetHeaderOcrWorker();
      const headerResult = await recognizeVehicleHeader(headerWorker, file);
      parsed = buildParsedResult(fullResult, headerResult);
    }
    return parsed;
  } finally {
    await worker.terminate();
    if (headerWorker) await headerWorker.terminate();
  }
}

// Batch OCR reuses a single Tesseract worker. This is much faster and lighter
// than initializing rus+est+eng once per screenshot when importing a fleet.
export async function parseVehicleScreenshotsInBrowser(files, callbacks = {}) {
  const list = Array.from(files || []);
  list.forEach(validateScreenshotFile);
  if (!list.length) return [];

  let activeIndex = -1;
  const progressRef = {
    current: (progress) => {
      if (activeIndex >= 0) callbacks.onProgress?.(activeIndex, progress);
    },
  };
  const worker = await createFleetOcrWorker(progressRef);
  let headerWorker = null;
  const results = [];

  try {
    for (let index = 0; index < list.length; index += 1) {
      activeIndex = index;
      callbacks.onStart?.(index);
      try {
        const fullResult = await worker.recognize(list[index]);
        let parsed = buildParsedResult(fullResult);

        if (needsIdentityFallback(parsed)) {
          if (!headerWorker) headerWorker = await createFleetHeaderOcrWorker();
          const headerResult = await recognizeVehicleHeader(headerWorker, list[index]);
          parsed = buildParsedResult(fullResult, headerResult);
        }

        results[index] = parsed;
        callbacks.onResult?.(index, parsed);
      } catch (error) {
        results[index] = null;
        callbacks.onError?.(index, error);
      }
    }
    return results;
  } finally {
    await worker.terminate();
    if (headerWorker) await headerWorker.terminate();
  }
}
